import * as _ from 'lodash';
import * as moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { ISwapDonorLookupData } from '@nkr/store/index';
import { ICenterBase, ICenterRecipient, IListItem, ISwapDonor } from '@nkr/models/index';
import { DonationType, DonorType, isVoucher, isChainStarter, MatchStatus } from '@nkr/enums/index';
import { Alert, Button, Col, ControlLabel, FormControl, Label, OverlayTrigger, Panel, Radio } from 'react-bootstrap';
import { FormGroupWrapper, ViewWrapper } from './styles';
import MaskedInput from 'react-maskedinput';
import NotificationDetails from './notification-details';
import PopoverTemplate from '@nkr/components/popover-template';
import LinkedRecipientSelect from './linked-recipient-select'
import AllowDuplicateControl from '@nkr/views/allow-duplicate/allow-duplicate-control';
import AddBackCreditReasonModal from './add-back-credit-reason-modal';
import { EntityType } from '@nkr/enums/entity-type';

interface IProps {
    isAdmin: boolean,
    isSuper: boolean,
    data: ISwapDonor,
    isPaired: boolean,
    recipientList: ICenterRecipient[],
    centerList: IListItem[],
    donationTypeList: IListItem[],
    rdnCenterList: ICenterBase[],
    lookupData: ISwapDonorLookupData,
    donorTypeList: any[],
    onDataChange: (data: ISwapDonor) => void,
    onSave: (data: ISwapDonor) => void,
}

const View = (props: IProps) => {
    const [showNotification, setShowNotification] = useState(false);
    const [notificationType, setNotificationType] = useState(-1);
    const [donationStartDateValid, setDonationStartDateValid] = useState(true);
    const [donationEndDateValid, setDonationEndDateValid] = useState(true);
    const [birthDateValid, setBirthDateValid] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [addBackCreditPrompt, setAddBackCreditPrompt] = useState({ show: false, changingTo: false });

    const ref = useRef();

    useEffect(() => {
        if (props.data == null) {
            setDonationStartDateValid(true);
            setDonationEndDateValid(true);
            setBirthDateValid(true);
            setDirty(false);
        }
    }, [props.data]);

    const handleFieldChange = (e: React.FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let id = e.currentTarget.id;
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;
        let data = _.cloneDeep(props.data);

        if (id == 'fullName') {
            data.fullName = value;
        }

        if (id == 'email') {
            data.email = value;
        }

        if (id == 'alias') {
            data.alias = value;
        }

        if (id == 'mrn') {
            data.mrn = value;
        }

        if (id == 'statusId') {
            data.statusId = parseInt(value);
        }

        if (id == 'registered') {
            data.registered = value;
        }

        if (id == 'firstExportableDate') {
            data.firstExportableDate = value;
        }

        if (id == 'birthDate') {
            data.birthDate = value;
        }

        if (id == 'donationStartDate') {
            data.donationStartDate = value;
        }

        if (id == 'donationEndDate') {
            data.donationEndDate = value;
        }

        if (name == 'isDeleted') {
            data.isDeleted = value == '1';
        }

        if (name == 'isActive') {
            data.isActive = value == '1';
        }

        if (name == 'gender') {
            data.gender = value;
        }

        if (name == 'bloodType') {
            data.bloodType = value;
        }

        if (name == 'rdnCenterId') {
            data.rdnCenterId = parseInt(value);
        }

        if (name == 'sendComplianceEmails') {
            data.sendComplianceEmails = value == '1';
        }

        if (name == 'isInDonorFollowUpProgram') {
            data.isInDonorFollowUpProgram = value == '1';
        }

        if (id == 'centerId') {
            data.centerId = parseInt(value)
        }

        if (id == 'donationTypeId') {
            data.donationTypeId = parseInt(value)
        }

        if (id == 'donorChainId') {
            data.donorChainId = parseInt(value)
        }

        if (id == 'clusterNumber') {
            data.clusterNumber = parseInt(value)
        }

        if (id == 'position') {
            data.position = parseInt(value)
        }

        if (id == 'expertSystemStatusId') {
            data.expertSystemStatusId = parseInt(value);
        }

        if (name == 'isRealTimeSwapFailure') {
            data.isRealTimeSwapFailure = value == '1';
        }

        props.onDataChange(data);
    };

    const handleAddBackCreditChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let changingTo = e.currentTarget.value == '1';

        if (changingTo == props.data.isAddBackCredit) {
            return;
        }

        setAddBackCreditPrompt({ show: true, changingTo });
    };

    const handleAddBackCreditConfirm = (reason: string) => {
        let data = _.cloneDeep(props.data);

        data.isAddBackCredit = addBackCreditPrompt.changingTo;
        data.addBackCreditChangeReason = reason;

        setAddBackCreditPrompt({ show: false, changingTo: false });

        props.onDataChange(data);
    };

    const handleAddBackCreditCancel = () => {
        setAddBackCreditPrompt({ show: false, changingTo: false });
    };

    const handleDataChange = (centerId: number, donorId: number, recipientId: number) => {
        let data = _.cloneDeep(props.data);

        if (props.data.donationTypeId == DonationType.StandardVoucher || props.data.donationTypeId == DonationType.FamilyVoucher) {
            data.adpRecipientId = recipientId;
        } else {
            data.recipientId = recipientId;
        }

        props.onDataChange(data);
    };

    const handleSave = () => {
        let { donationStartDateValid, donationEndDateValid, birthDateValid } = validate();

        let isValid = donationStartDateValid && donationEndDateValid && birthDateValid;

        let data = _.cloneDeep(props.data);

        setDonationStartDateValid(donationEndDateValid);
        setDonationEndDateValid(donationEndDateValid);
        setBirthDateValid(birthDateValid);
        setDirty(true);

        if (isValid) {
            props.onSave(data);
        }
    };

    const validate = () => {
        let { donationStartDate, donationEndDate, originalFromDate, originalToDate, birthDate, donationTypeId, isActive, statusId, donorType, recipientId } = props.data;

        let startDate = moment(donationStartDate);
        let startDateAnd3 = startDate.clone().add(3, 'week');
        let endDate = moment(donationEndDate);
        let threeWeeksFromToday = moment().add(3, 'week').startOf('day');

        let donationStartDateValid = true;
        let donationEndDateValid = true;
        let birthDateValid = true;

        birthDateValid = moment(birthDate).isValid();

        let today = moment();
        let age = today.diff(birthDate, 'years');

        if (age < 18 || age > 100) {
            birthDateValid = false;
        }

        if (props.isAdmin == false && checkShowDonation()) {
            donationStartDateValid = startDate.isValid();
            donationEndDateValid = endDate.isValid() && startDate.isSameOrBefore(endDate);
            birthDateValid = moment(birthDate).isValid();

            let today = moment();
            let age = today.diff(birthDate, 'years');

            if (age < 18 || age > 100) {
                birthDateValid = false;
            }

            //For SV, the start date MUST be 3 weeks from today, and the end date MUST be 3 weeks from the start date
            if (props.data.donationTypeId == DonationType.StandardVoucher) {
                if (donationStartDate != originalFromDate && donationEndDate != originalToDate) {
                    if (startDate.isValid() && endDate.isValid()) {
                        donationStartDateValid = startDate.isSameOrAfter(threeWeeksFromToday);
                        donationEndDateValid = endDate.isSameOrAfter(startDateAnd3);
                    }
                }
            }

            //must be within 6 week range if voucher donor
            if (isVoucher(donationTypeId)) {
                let startDate = moment(donationStartDate).clone();
                let endDate = moment(donationEndDate).clone();

                if (startDate.add(6, 'weeks').isBefore(endDate) || !startDate.isValid() || !endDate.isValid()) {
                    donationStartDateValid = false;
                    donationEndDateValid = false;
                }
            }

            if (isChainStarter(donationTypeId)) {
                let today = moment();
                let existingStart = moment(originalFromDate).clone();
                let existingEnd = moment(originalToDate).clone();
                let newStart = moment(donationStartDate).clone();
                let newEnd = moment(donationEndDate).clone();
                let isChange = !existingStart.isSame(newStart) || !existingEnd.isSame(newEnd);

                //validation for NDD and FV setting back to original dates and throwing error if not within required range
                if (donationTypeId == DonationType.NonDirectedDonor || donationTypeId == DonationType.FamilyVoucher) {
                    if ((!newStart.isSameOrAfter(today) || !newEnd.isSameOrAfter(newStart)) && isChange) {
                        //set back to original date
                        props.data.donationStartDate = originalFromDate;
                        props.data.donationEndDate = originalToDate;
                        props.onDataChange(props.data);

                        donationStartDateValid = false;
                        donationEndDateValid = false;
                    }
                }
            }

            if (isActive && statusId < MatchStatus.Transplanted && donorType == DonorType.NonDirectedDonor) {
                let existingStart = moment(originalFromDate).clone();
                let existingEnd = moment(originalToDate).clone();
                let newStart = moment(donationStartDate).clone();
                let newEnd = moment(donationEndDate).clone();
                let today = moment().clone();

                if (!existingStart.isSame(newStart) || !existingEnd.isSame(newEnd)) {
                    if (newStart.isValid() == false || newStart.isBefore(today)) {
                        donationStartDateValid = false;
                    }

                    if (newEnd.isBefore(newStart) || !newEnd.isValid()) {
                        donationEndDateValid = false;
                    }
                }
            }
        }

        return {
            donationStartDateValid,
            donationEndDateValid,
            birthDateValid
        };
    };

    const checkShowDonation = () => {
        let { data: { donationTypeId, isActive } } = props;
        return [DonationType.StandardVoucher, DonationType.FamilyVoucher, DonationType.NonDirectedDonor, DonationType.RealTimeSwapFailure, DonationType.SwapSaver].indexOf(donationTypeId) >= 0 && isActive;
    };

    const handleAddEditCancel = () => {
        setShowNotification(false);
        setNotificationType(-1);
    };

    if (props.data == null && props.lookupData == null)
        return null;

    const isExportable = props.data.isExportable;
    const isProfileCompliant = props.data.donorCompliance.complianceIssues.length == 0;
    const areVouchersComplete = props.data.adpVoucherHolders.length > 0 ? props.data.adpVoucherHolders.filter(x => x.currentSelfRegistrationStep < 300).length == 0 : false;
    const showDonation = checkShowDonation()
    const isValid = dirty && showDonation ? (donationStartDateValid && donationEndDateValid && birthDateValid) : true;

    let recipientId = props.data.recipientId;

    if (props.data.donationTypeId == DonationType.StandardVoucher || props.data.donationTypeId == DonationType.FamilyVoucher) {
        recipientId = props.data.adpRecipientId;
    }

    let recipientList = [{
        id: 0,
        alias: '- Select a recipient to pair -',
        centerId: 0,
        centerAlias: '',
        chip: false
    }, ...props.recipientList]

    return (
        <>
            <ViewWrapper horizontal ref={ref}>
                <div className='modal-container'>
                    {!isValid && (!donationStartDateValid || !donationEndDateValid) ?
                        <div className='row'>
                            <div className='col-xs-12 col-lg-12'>
                                <Alert bsStyle='danger'>
                                    <div style={{ marginBottom: 5 }}>Please enter valid donation start/end dates.</div>

                                    {props.isAdmin ?
                                        <>
                                            <div style={{ marginBottom: 5 }}>Donation dates are required and cannot be earlier than the current date</div>
                                            <ul>
                                                <li style={{ listStyleType: 'disc' }}>Donation range must be no more than 42 days total</li>
                                            </ul>
                                        </>
                                        :
                                        <div style={{ marginBottom: 5 }}>
                                            Donation Range Rules
                                            <ul>
                                                <li style={{ listStyleType: 'disc' }}>NDD and FV must have dates from today's date, onwards. Otherwise, the dates are reverted to their previous values</li>
                                                <li style={{ listStyleType: 'disc' }}>For SV, the start date must be 3 weeks from today, and the end date must be 3 weeks from the start date</li>
                                                <li style={{ listStyleType: 'disc' }}>Donation range must be no more than 42 days total</li>
                                            </ul>
                                        </div>
                                    }

                                    <div>Please ensure all entered or required fields are valid.</div>
                                </Alert>
                            </div>
                        </div>
                        :
                        <>
                            {!isValid && !birthDateValid &&
                                <div className='row'>
                                    <div className='col-xs-12 col-lg-12'>
                                        <Alert bsStyle='danger'>
                                            <div style={{ marginBottom: 5 }}>Please enter valid birth date.</div>
                                            <>
                                                <div style={{ marginBottom: 5 }}>Birth date must be between 18 and 100 years ago</div>
                                            </>
                                        </Alert>
                                    </div>
                                </div>
                            }
                        </>
                    }

                    <Panel>
                        <Panel.Body style={{ padding: '10px 5px' }}>
                            {props.isAdmin &&
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Managing Center:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            componentClass='select'
                                            id='centerId'
                                            value={props.data.centerId}
                                            onChange={handleFieldChange}
                                        >
                                            {props.lookupData.centerList.map(item =>
                                                <option value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroupWrapper>
                            }

                            {!props.isAdmin ?
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Donation Type:</Col>
                                    <Col xs={12} lg={7}><div className='form-control-static'>{props.data.donationType}</div></Col>
                                </FormGroupWrapper>
                                :
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Donation Type:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            componentClass='select'
                                            id='donationTypeId'
                                            value={props.data.donationTypeId}
                                            onChange={handleFieldChange}
                                        >
                                            {props.lookupData.donationList.map(item =>
                                                <option value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroupWrapper>
                            }


                            {!props.isAdmin ?
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Alias:</Col>
                                    <Col xs={12} lg={7}><div className='form-control-static'>{props.data.alias}</div></Col>
                                </FormGroupWrapper>
                                :
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Alias:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            type='text'
                                            id='alias'
                                            maxLength={12}
                                            value={props.data.alias}
                                            onChange={handleFieldChange}
                                        />
                                    </Col>
                                </FormGroupWrapper>
                            }

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>MRN:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        type='text'
                                        id='mrn'
                                        value={props.data.mrn}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Full Name:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        type='text'
                                        id='fullName'
                                        value={props.data.fullName}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Email:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        type='text'
                                        id='email'
                                        value={props.data.email}
                                        onChange={handleFieldChange}
                                    />
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper validationState={!birthDateValid ? 'error' : null}>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Date of Birth:</Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5 }}>
                                            <MaskedInput
                                                id='birthDate'
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                className='form-control'
                                                value={props.data.birthDate}
                                                onChange={handleFieldChange} />
                                        </div>
                                    </div>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Gender:</Col>
                                <Col xs={12} lg={7}>
                                    <Radio inline id='genderM' name='gender' value='M' checked={props.data.gender == 'M'} onChange={handleFieldChange}>Male</Radio>
                                    <Radio inline id='genderF' name='gender' value='F' checked={props.data.gender == 'F'} onChange={handleFieldChange}>Female</Radio>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Blood Type:</Col>
                                <Col xs={12} lg={7}><div className='form-control-static'>{props.data.bloodType}</div></Col>
                            </FormGroupWrapper>

                            {!props.isAdmin ?
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Status:</Col>
                                    <Col xs={12} lg={7}><div className='form-control-static'>{props.data.status}</div></Col>
                                </FormGroupWrapper>
                                :
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Status:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            componentClass='select'
                                            id='statusId'
                                            value={props.data.statusId}
                                            onChange={handleFieldChange}
                                        >
                                            {props.lookupData.statusList.map(item =>
                                                <option value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroupWrapper>
                            }

                            {props.isAdmin && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Expert System Status:</Col>
                                    <Col xs={12} lg={7}>
                                        <FormControl
                                            disabled={!props.data.isExpertSystem}
                                            componentClass='select'
                                            id='expertSystemStatusId'
                                            value={props.data.expertSystemStatusId}
                                            onChange={handleFieldChange}
                                        >
                                            {props.lookupData.expertSystemStatusList.map(item =>
                                                <option value={item.id}>{item.description}</option>
                                            )}
                                        </FormControl>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            {props.data.isTransplanted == false &&
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Exportable:</Col>
                                    <Col xs={12} lg={7}>
                                        <div className='form-control-static'>
                                            {!isExportable && (
                                                <Label bsStyle='danger' style={{ fontSize: '100%' }}>No</Label>
                                            )}

                                            {isExportable && (
                                                <Label bsStyle='success' style={{ fontSize: '100%' }}>Yes</Label>
                                            )}

                                            {!isExportable && (
                                                <OverlayTrigger trigger={['click', 'click']} placement='bottom' container={ref.current} rootClose overlay={
                                                    <PopoverTemplate width='400px'>
                                                        <div style={{ fontWeight: 'bold', marginBottom: 5 }}>
                                                            This donor is not exportable for the following reason(s):
                                                        </div>

                                                        <div style={{ maxHeight: 400, overflowY: 'scroll', border: '1px solid #ddd', padding: 5, borderRadius: 4 }}>
                                                            <ul style={{ paddingLeft: 23 }}>
                                                                {props.data.exportabilityValidationResults.map((item, index) =>
                                                                    <li key={index} style={{ listStyleType: 'disc' }}>{item}</li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    </PopoverTemplate>
                                                }>
                                                    <a style={{ marginLeft: 20, cursor: 'default' }}>click here for details</a>
                                                </OverlayTrigger>
                                            )}
                                        </div>
                                    </Col>
                                </FormGroupWrapper>
                            }

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Is Active:</Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ paddingBottom: 7 }}>
                                        <Radio inline id='isActiveYes' name='isActive' value={1} checked={props.data.isActive} onChange={handleFieldChange}>Yes</Radio>
                                        <Radio inline id='isActiveNo' name='isActive' value={0} checked={!props.data.isActive} onChange={handleFieldChange}>No</Radio>
                                    </div>
                                </Col>
                            </FormGroupWrapper>

                            {props.isSuper &&
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Allow Duplicate:</Col>
                                    <Col xs={12} lg={7}>
                                        <AllowDuplicateControl entityType={EntityType.Donor} profileId={props.data.donorId} />
                                    </Col>
                                </FormGroupWrapper>
                            }

                            {props.isAdmin && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Is Real Time Swap Failure?</Col>
                                    <Col xs={12} lg={7}>
                                        <Radio inline id='isRealTimeSwapFailureYes' name='isRealTimeSwapFailure' value={1} checked={props.data.isRealTimeSwapFailure} onChange={handleFieldChange}>Yes</Radio>
                                        <Radio inline id='isRealTimeSwapFailureNo' name='isRealTimeSwapFailure' value={0} checked={!props.data.isRealTimeSwapFailure} onChange={handleFieldChange}>No</Radio>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            {props.isAdmin && (
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Add Back Credit?</Col>
                                    <Col xs={12} lg={7}>
                                        <Radio inline id='isAddBackCreditYes' name='isAddBackCredit' value={1} checked={props.data.isAddBackCredit} onChange={handleAddBackCreditChange}>Yes</Radio>
                                        <Radio inline id='isAddBackCreditNo' name='isAddBackCredit' value={0} checked={!props.data.isAddBackCredit} onChange={handleAddBackCreditChange}>No</Radio>
                                    </Col>
                                </FormGroupWrapper>
                            )}

                            {(props.data.isActive || props.data.isTransplanted) &&
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Profile Compliant:</Col>
                                    <Col xs={12} lg={7}>
                                        <div className='form-control-static'>
                                            {!isProfileCompliant &&
                                                <Label bsStyle='danger' style={{ fontSize: '100%' }}>No</Label>
                                            }

                                            {isProfileCompliant &&
                                                <Label bsStyle='success' style={{ fontSize: '100%' }}>Yes</Label>
                                            }
                                        </div>
                                    </Col>
                                </FormGroupWrapper>
                            }

                            {(props.data.donationTypeId == DonationType.StandardVoucher || props.data.donationTypeId == DonationType.FamilyVoucher) &&
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Vouchers Complete:</Col>
                                    <Col xs={12} lg={7}>
                                        <div className='form-control-static'>
                                            {!areVouchersComplete && (
                                                <Label bsStyle='danger' style={{ fontSize: '100%' }}>No</Label>
                                            )}

                                            {areVouchersComplete && (
                                                <Label bsStyle='success' style={{ fontSize: '100%' }}>Yes</Label>
                                            )}

                                            {!areVouchersComplete && (
                                                <a href={`center/${props.data.centerId}/donor/${props.data.donorId}/profile/vouchers`} style={{ marginLeft: 20, cursor: 'default' }}>click here for details</a>
                                            )}
                                        </div>
                                    </Col>
                                </FormGroupWrapper>
                            }

                            {props.isAdmin &&
                                <FormGroupWrapper>
                                    <Col xs={12} lg={5} componentClass={ControlLabel}>Linked Recipient:</Col>
                                    <Col xs={12} lg={7}>
                                        <LinkedRecipientSelect
                                            centerId={props.data.centerId}
                                            donorId={props.data.donorId}
                                            recipientId={recipientId}
                                            recipientList={recipientList}
                                            onClick={handleDataChange}
                                        />
                                    </Col>
                                </FormGroupWrapper>
                            }

                            {showDonation && (
                                <>
                                    <FormGroupWrapper validationState={!donationStartDateValid ? 'error' : null}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Donation Start Date:</Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ marginRight: 5, width: '100%' }}>
                                                    <MaskedInput
                                                        id='donationStartDate'
                                                        mask='11/11/1111'
                                                        placeholder='mm/dd/yyyy'
                                                        className='form-control'
                                                        value={props.data.donationStartDate}
                                                        disable={!showDonation}
                                                        onChange={handleFieldChange} />
                                                </div>
                                            </div>
                                        </Col>
                                    </FormGroupWrapper>

                                    <FormGroupWrapper validationState={!donationEndDateValid ? 'error' : null}>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Donation End Date:</Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <div style={{ marginRight: 5, width: '100%' }}>
                                                    <MaskedInput
                                                        id='donationEndDate'
                                                        mask='11/11/1111'
                                                        placeholder='mm/dd/yyyy'
                                                        className='form-control'
                                                        value={props.data.donationEndDate}
                                                        disable={!showDonation}
                                                        onChange={handleFieldChange} />
                                                </div>
                                            </div>
                                        </Col>
                                    </FormGroupWrapper>
                                </>
                            )}

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Registration Date:</Col>
                                <Col xs={12} lg={7}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ marginRight: 5, width: '100%' }}>
                                            <MaskedInput
                                                id='registered'
                                                disabled
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                className='form-control'
                                                value={props.data.registered}
                                                onChange={handleFieldChange} />
                                        </div>
                                    </div>
                                </Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>First Exportable Date:</Col>
                                <Col xs={12} lg={7}><div className='form-control-static'>{props.data.firstExportableDate != '' ? props.data.firstExportableDate : '--'}</div></Col>
                            </FormGroupWrapper>

                            <FormGroupWrapper>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>RDN Managing Center:</Col>
                                <Col xs={12} lg={7}>
                                    <FormControl
                                        componentClass='select'
                                        name='rdnCenterId'
                                        value={props.data.rdnCenterId}
                                        onChange={handleFieldChange}
                                    >
                                        <option value={0}>- Not a Remote Donor -</option>
                                        {props.rdnCenterList.map(item =>
                                            <option key={'rdn-ctr-' + item.id} value={item.id}>{item.alias}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroupWrapper>

                            {props.isAdmin &&
                                <>
                                    <FormGroupWrapper>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Include in Data Compliance:</Col>
                                        <Col xs={12} lg={7}>
                                            <Radio inline id='sendComplianceEmailsYes' name='sendComplianceEmails' value={1} checked={props.data.sendComplianceEmails} onChange={handleFieldChange}>Yes</Radio>
                                            <Radio inline id='sendComplianceEmailsNo' name='sendComplianceEmails' value={0} checked={!props.data.sendComplianceEmails} onChange={handleFieldChange}>No</Radio>
                                        </Col>
                                    </FormGroupWrapper>

                                    <FormGroupWrapper>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>NKR Donor Followup Program:</Col>
                                        <Col xs={12} lg={7}>
                                            <Radio inline id='isInDonorFollowUpProgramYes' name='isInDonorFollowUpProgram' value={1} checked={props.data.isInDonorFollowUpProgram} onChange={handleFieldChange}>Yes</Radio>
                                            <Radio inline id='isInDonorFollowUpProgramNo' name='isInDonorFollowUpProgram' value={0} checked={!props.data.isInDonorFollowUpProgram} onChange={handleFieldChange}>No</Radio>
                                        </Col>
                                    </FormGroupWrapper>
                                </>
                            }
                        </Panel.Body>
                    </Panel>

                    {props.isAdmin &&
                        <>
                            <Panel>
                                <Panel.Heading>Admin Only</Panel.Heading>
                                <Panel.Body>
                                    <FormGroupWrapper>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Donor Is Deleted:</Col>
                                        <Col xs={12} lg={7}>
                                            <div style={{ paddingBottom: 7 }}>
                                                <Radio inline id='isDeletedYes' name='isDeleted' value={'1'} checked={props.data.isDeleted} onChange={handleFieldChange}>Yes</Radio>
                                                <Radio inline id='isDeletedNo' name='isDeleted' value={'0'} checked={!props.data.isDeleted} onChange={handleFieldChange}>No</Radio>
                                            </div>
                                        </Col>
                                    </FormGroupWrapper>
                                    <FormGroupWrapper>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Chain Number: </Col>
                                        <Col xs={12} lg={7}>
                                            <FormControl
                                                componentClass='select'
                                                id='donorChainId'
                                                value={props.data.donorChainId}
                                                onChange={handleFieldChange}
                                            >
                                                <option value={0}>Please Select </option>
                                                {props.lookupData.chainList.map(item =>
                                                    <option value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroupWrapper>
                                    <FormGroupWrapper>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Cluster:</Col>
                                        <Col xs={12} lg={7}>
                                            <FormControl
                                                componentClass='select'
                                                id='clusterNumber'
                                                value={props.data.clusterNumber}
                                                onChange={handleFieldChange}
                                            >
                                                <option value={0}>Please Select </option>
                                                {props.lookupData.clusterList.map(item =>
                                                    <option value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroupWrapper>
                                    <FormGroupWrapper>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}>Position:</Col>
                                        <Col xs={12} lg={7}>
                                            <FormControl
                                                componentClass='select'
                                                id='position'
                                                value={props.data.position}
                                                onChange={handleFieldChange}
                                            >
                                                <option value={0}>Please Select </option>
                                                {props.lookupData.positionList.map(item =>
                                                    <option value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroupWrapper>
                                </Panel.Body>
                            </Panel>
                        </>
                    }

                    <div style={{ textAlign: 'center' }}>
                        <Button bsStyle='primary' bsSize='large' onClick={handleSave}>Save Changes</Button>

                        {/* {props.data.isAdp && props.data.adpRecipientId != 0 && props.data.adpRecipientId != null &&
                            <span style={{ paddingLeft: 10 }}><Button onClick={handleShowUnlinkModal}>Unlink Recipient</Button></span>
                        } */}
                    </div>
                </div>
            </ViewWrapper>

            <NotificationDetails
                show={showNotification}
                adpVoucherHolders={props.data.adpVoucherHolders}
                exportabilityResults={props.data.exportabilityValidationResults}
                donorCompliance={props.data.donorCompliance}
                type={notificationType}
                onCancel={handleAddEditCancel} />

            <AddBackCreditReasonModal
                show={addBackCreditPrompt.show}
                alias={props.data.alias}
                changingTo={addBackCreditPrompt.changingTo}
                onConfirm={handleAddBackCreditConfirm}
                onCancel={handleAddBackCreditCancel} />
        </>
    );
}

export default View;